import type { ReactNode } from 'react'

export type ChipTone = 'ok' | 'warn' | 'bad' | 'info' | 'dispatch' | 'tech' | 'neutral' | 'brand'

export interface ChipProps {
  tone?: ChipTone
  children: ReactNode
  className?: string
}

const TONE_CLASSES: Record<ChipTone, string> = {
  ok: 'bg-ok-subtle text-ok',
  warn: 'bg-warn-subtle text-warn',
  bad: 'bg-bad-subtle text-bad',
  info: 'bg-info-subtle text-info',
  dispatch: 'bg-dispatch-subtle text-dispatch',
  // Fix round 2 (phase-2 whole-phase review, finding 7): the account menu's
  // Role pill needs the SAME token pair `Avatar`'s role colouring already
  // uses for `tech` (--role-tech / --role-tech-soft), so a role pill and a
  // role-coloured avatar for the same person never disagree.
  tech: 'bg-role-tech-subtle text-role-tech',
  // Fix round 2 (finding 12): was `bg-elevated` — the raised-surface token,
  // wrong on any card/menu already sitting on `bg-elevated` (e.g. the
  // account menu this pill's sibling lives in). `bg-sunken` genuinely
  // differs from `bg-elevated` in every theme (tokens.css, finding 3).
  neutral: 'bg-sunken text-mid',
  // Task 6: an identity marker (e.g. the Users pane's SuperAdmin pill,
  // README §7b), not a status — takes the brand tint slot rather than any
  // of the status tones above, none of which mean "who this is".
  brand: 'bg-tint-primary text-on-tint-primary',
}

export function Chip({ tone = 'neutral', children, className = '' }: ChipProps) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1 whitespace-nowrap rounded-[5px] px-2 py-0.5',
        'text-[11px] font-semibold',
        TONE_CLASSES[tone],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </span>
  )
}
